// ============================================================================
// SicurezzaAzienda.jsx
//
// Pagina "Sicurezza Azienda" per BertocProject Gestionale.
// Contiene i dati fissi aziendali usati per generare il POS (Piano Operativo
// di Sicurezza). Struttura: 7 sezioni accordion indipendenti, ognuna salvabile
// separatamente. I dati sono salvati in Firestore in:
//   impostazioni/sicurezza_azienda
//
// Precompilazione: al primo caricamento, se non esiste nulla in DB, usa i
// default estratti dal POS Poderino di aprile 2025.
//
// PROPS:
//   utentiDB : array utenti (per formazione e DPI)
//   onIndietro : function (torna al menu)
// ============================================================================

import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import DocumentiAziendaBox from './DocumentiAziendaBox';
import SchedeCFBox from './SchedeCFBox';
import MacchineBox from './MacchineBox';
import ModalitaBox from './ModalitaBox';
import RifiutiBox from './RifiutiBox'; // FIX_RIFIUTI_IN_SICUREZZA_v1 // FIX_MODALITA_IN_SICUREZZA_v1 // FIX_MACCHINE_IN_SICUREZZA_v1
import {doc, getDoc, setDoc} from "./firebase-shim";
import MagazzinoPonteggio from "./MagazzinoPonteggio"; // MAG_PONTEGGIO_v1
import InviaDocumentiBtn from "./InviaDocumenti"; // INVIO_DOC_v1

const theme = {
  bg: "#0f1117",
  surface: "#1a1d27",
  surfaceHover: "#22263a",
  border: "#2a2e45",
  accent: "#f5a623",
  text: "#e8eaf2",
  textMuted: "#7b82a0",
  textDim: "#4a5068",
  success: "#3ecf8e",
  info: "#378ADD",
  warning: "#D4537E",
  error: "#f06060",
};

const inputSt = {
  background: "#0f1117",
  border: `1px solid ${theme.border}`,
  borderRadius: 8,
  color: theme.text,
  padding: "10px 12px",
  fontSize: 14,
  fontFamily: "'DM Sans',sans-serif",
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};

const labelSt = {
  fontSize: 11,
  color: theme.textDim,
  textTransform: "uppercase",
  letterSpacing: 1,
  marginBottom: 6,
  fontWeight: 600,
};

const btnPrimarioSt = {
  background: theme.accent,
  color: "#1a1d27",
  fontWeight: 700,
  fontSize: 14,
  border: "none",
  borderRadius: 8,
  padding: "10px 16px",
  cursor: "pointer",
  fontFamily: "'DM Sans',sans-serif",
};

const btnSecondarioSt = {
  background: "transparent",
  color: theme.text,
  fontWeight: 500,
  fontSize: 14,
  border: `1px solid ${theme.border}`,
  borderRadius: 8,
  padding: "9px 14px",
  cursor: "pointer",
  fontFamily: "'DM Sans',sans-serif",
};

// ============================================================================
// DEFAULTS dal POS Poderino (aprile 2025)
// ============================================================================
const DEFAULTS = {
  impresa: {
    ragioneSociale: "BERTOC PROJECT SRL",
    sedeLegale: "Via Vecchia Provinciale Lucchese n. 47/C",
    sedeCitta: "Serravalle Pistoiese",
    sedeCap: "51030",
    sedeProvincia: "PT",
    pIva: "01747090478",
    codFiscale: "01747090478",
    rea: "PT 176101",
    aia: "PT 60028",
    inail: "18807164 - 2116591",
    email: "ufficio@bertocproject.it",
    pec: "bertocproject@legal.email.it",
    telefono: "3356939544",
    attivita: "Impresa edile (lavori edili in genere)",
    datoreDiLavoro: "Marco Bertocci (datore di lavoro e socio lavoratore)",
  },
  responsabili: {
    rspp: "Marco Bertocci",
    primoSoccorso: "Marco Bertocci",
    antincendio: "Marco Bertocci",
    rlsTipo: "aziendale",  // aziendale | territoriale | sito
    rls: "Edoardo Bertocci",
    medicoCompetente: "Dott. Manfredi Montalti",
    direttoreTecnico: "",
  },
  // Formazione: chiave nome operaio -> oggetto corsi
  formazione: {
    "Marco Bertocci":   { base: true, baseStranieri: false, rspp: true, primoSoccorso: true, antincendio: true, rls: false, gru: false, movTerra: false, carrelli: false, telescopico: false, ponteggi: false, lavoriQuota: false, dpi3cat: true, altro: "" },
    "Edoardo Bertocci": { base: true, baseStranieri: false, rspp: false, primoSoccorso: true, antincendio: true, rls: true, gru: false, movTerra: false, carrelli: false, telescopico: false, ponteggi: true, lavoriQuota: false, dpi3cat: false, altro: "" },
    "Leonardo Boschi":  { base: true, baseStranieri: false, rspp: false, primoSoccorso: false, antincendio: false, rls: false, gru: false, movTerra: false, carrelli: false, telescopico: false, ponteggi: true, lavoriQuota: false, dpi3cat: false, altro: "" },
    "Ion Birdeanu":     { base: true, baseStranieri: false, rspp: false, primoSoccorso: false, antincendio: false, rls: false, gru: false, movTerra: false, carrelli: false, telescopico: false, ponteggi: true, lavoriQuota: false, dpi3cat: false, altro: "" },
    "Ilie Moescu":      { base: true, baseStranieri: false, rspp: false, primoSoccorso: true, antincendio: false, rls: false, gru: false, movTerra: false, carrelli: false, telescopico: false, ponteggi: true, lavoriQuota: false, dpi3cat: false, altro: "" },
    "Aamir Shahzad":    { base: false, baseStranieri: true, rspp: false, primoSoccorso: false, antincendio: false, rls: false, gru: false, movTerra: false, carrelli: false, telescopico: false, ponteggi: true, lavoriQuota: false, dpi3cat: false, altro: "" },
    "Mahamadou Niakate":{ base: false, baseStranieri: true, rspp: false, primoSoccorso: false, antincendio: false, rls: false, gru: false, movTerra: false, carrelli: false, telescopico: false, ponteggi: true, lavoriQuota: false, dpi3cat: false, altro: "" },
  },
  // DPI: chiave nome operaio -> oggetto DPI
  dpi: {
    "Marco Bertocci":    { casco: true, scarpe: true, guanti: true, occhiali: true, otoprotettori: true, facciali: true, dpi3cat: false, indumenti: false, altaVisibilita: true, visieraSald: false, dpiSpecifici: false },
    "Edoardo Bertocci":  { casco: true, scarpe: true, guanti: true, occhiali: true, otoprotettori: true, facciali: true, dpi3cat: false, indumenti: false, altaVisibilita: true, visieraSald: false, dpiSpecifici: false },
    "Leonardo Boschi":   { casco: true, scarpe: true, guanti: true, occhiali: true, otoprotettori: true, facciali: true, dpi3cat: false, indumenti: false, altaVisibilita: true, visieraSald: false, dpiSpecifici: false },
    "Ion Birdeanu":      { casco: true, scarpe: true, guanti: true, occhiali: true, otoprotettori: true, facciali: true, dpi3cat: true, indumenti: false, altaVisibilita: true, visieraSald: false, dpiSpecifici: false },
    "Ilie Moescu":       { casco: true, scarpe: true, guanti: true, occhiali: true, otoprotettori: true, facciali: true, dpi3cat: false, indumenti: false, altaVisibilita: true, visieraSald: false, dpiSpecifici: false },
    "Aamir Shahzad":     { casco: true, scarpe: true, guanti: true, occhiali: true, otoprotettori: true, facciali: true, dpi3cat: false, indumenti: false, altaVisibilita: true, visieraSald: false, dpiSpecifici: false },
    "Mahamadou Niakate": { casco: true, scarpe: true, guanti: true, occhiali: true, otoprotettori: true, facciali: true, dpi3cat: false, indumenti: false, altaVisibilita: true, visieraSald: false, dpiSpecifici: false },
  },
  ponteggio: {
    marca: "",
    modello: "",
    nAutorizzazione: "",
    proprieta: "proprio",
  },
  attrezzature: {
    // lista dal POS Poderino - solo quelle ⌧
    molatrice: { presente: true, libretto: false, collaudo: false, verifiche: false, usoComune: false },
    avvitatore: { presente: true, libretto: false, collaudo: false, verifiche: false, usoComune: false },
    tassellatore: { presente: true, libretto: false, collaudo: false, verifiche: false, usoComune: false },
    trabattello: { presente: true, libretto: false, collaudo: false, verifiche: false, usoComune: false },
    autocarri: { presente: true, libretto: false, collaudo: false, verifiche: false, usoComune: false },
  },
  documenti: {
    durc: { scadenza: "" },
    visura: { scadenza: "" },
    inail: { scadenza: "" },
    inps: { scadenza: "" },
    cassaEdile: { scadenza: "" },
  },
};

// Lista nomi per tabelle formazione e DPI
const OPERAI_POS = [
  "Marco Bertocci",
  "Edoardo Bertocci",
  "Leonardo Boschi",
  "Ion Birdeanu",
  "Ilie Moescu",
  "Aamir Shahzad",
  "Mahamadou Niakate",
];

// Colonne formazione (ordine tabella POS)
const COLONNE_FORMAZIONE = [
  { key: "base",           label: "Base",       full: "Corso base art. 37" },
  { key: "baseStranieri",  label: "Base str.",  full: "Corso base stranieri art. 37" },
  { key: "rspp",           label: "RSPP",       full: "RSPP Datori di Lavoro art. 34" },
  { key: "primoSoccorso",  label: "P. Socc.",   full: "Addetto primo soccorso" },
  { key: "antincendio",    label: "Antinc.",    full: "Addetto antincendio ed evacuazione" },
  { key: "rls",            label: "RLS",        full: "RLS art. 37" },
  { key: "gru",            label: "Gru",        full: "Gru/Autogru art. 37" },
  { key: "movTerra",       label: "Mov.Terra",  full: "Macchine movimento terra art. 37" },
  { key: "carrelli",       label: "Carrelli",   full: "Carrelli elevatori art. 37" },
  { key: "telescopico",    label: "Telesc.",    full: "Carrello telescopico art. 37" },
  { key: "ponteggi",       label: "Pont.",      full: "Ponteggi art. 136" },
  { key: "lavoriQuota",    label: "Quota",      full: "Lavori in quota art. 116" },
  { key: "dpi3cat",        label: "DPI 3°",     full: "DPI 3ª categoria art. 77" },
];

const COLONNE_DPI = [
  { key: "casco",          label: "Casco",      full: "Casco / elmetto protettivo" },
  { key: "scarpe",         label: "Scarpe",     full: "Scarpe antinfortunistiche" },
  { key: "guanti",         label: "Guanti",     full: "Guanti protettivi" },
  { key: "occhiali",       label: "Occhiali",   full: "Occhiali / maschere / schermi" },
  { key: "otoprotettori",  label: "Otopr.",     full: "Otoprotettori (cuffie/tappi)" },
  { key: "facciali",       label: "Facciali",   full: "Facciali / maschere con filtro" },
  { key: "dpi3cat",        label: "DPI 3°",     full: "DPI 3ª categoria (imbracature)" },
  { key: "indumenti",      label: "Indum.",     full: "Indumenti protettivi" },
  { key: "altaVisibilita", label: "Alta vis.",  full: "Indumenti alta visibilità" },
  { key: "visieraSald",    label: "Vis. sald.", full: "Visiera per saldatura" },
  { key: "dpiSpecifici",   label: "Specifici",  full: "DPI per rischi specifici" },
];

const ATTREZZATURE_LISTA = [
  { key: "pistolaSparachiodi", label: "Pistola sparachiodi" },
  { key: "molatrice", label: "Molatrice portatile (Flessibile)" },
  { key: "avvitatore", label: "Avvitatore elettrico" },
  { key: "tassellatore", label: "Tassellatore" },
  { key: "trapano", label: "Trapano perforatore/demolitore" },
  { key: "seghettoAlt", label: "Seghetto alternativo" },
  { key: "segaCircolare", label: "Sega circolare" },
  { key: "spruzzatriceIntonaci", label: "Spruzzatrice per intonaci" },
  { key: "taglialaterizio", label: "Taglia laterizio-piastrelle" },
  { key: "martelloDemolitore", label: "Martello demolitore" },
  { key: "miscelatriceMalte", label: "Miscelatrice per malte" },
  { key: "saldatriceElettrica", label: "Saldatrice elettrica" },
  { key: "spruzzatricePitture", label: "Spruzzatrice per pitture" },
  { key: "miscelatrice", label: "Miscelatrice" },
  { key: "compressore", label: "Compressore" },
  { key: "trabattello", label: "Ponte su ruote (Trabattello)" },
  { key: "ponteggi", label: "Ponteggi metallici fissi" },
  { key: "ponteCavalletti", label: "Ponte su cavalletti" },
  { key: "autocarri", label: "Autocarri" },
  { key: "cestelloIdraulico", label: "Cestello idraulico" },
  { key: "gruppoElettrogeno", label: "Gruppo elettrogeno" },
  { key: "lampadaPortatile", label: "Lampada portatile" },
];

// ============================================================================
// COMPONENTE PRINCIPALE
// ============================================================================
export default function SicurezzaAzienda({ utentiDB = [], onIndietro, onNavigate }) {

  const [dati, setDati] = useState(null);      // null = in caricamento
  const [loading, setLoading] = useState(true);
  const [sezioneAperta, setSezioneAperta] = useState(null); // id sezione
  const [salvandoSezione, setSalvandoSezione] = useState(null);
  const [ultimoSalvataggio, setUltimoSalvataggio] = useState({});  // {sezione: timestamp}
  const [errore, setErrore] = useState(null);

  // CARICAMENTO INIZIALE
  useEffect(() => {
    async function carica() {
      try {
        const ref = doc(db, 'impostazioni', 'sicurezza_azienda');
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const letti = snap.data();
          // Merge con defaults (per gestire campi nuovi aggiunti dopo)
          setDati({
            impresa: { ...DEFAULTS.impresa, ...(letti.impresa || {}) },
            responsabili: { ...DEFAULTS.responsabili, ...(letti.responsabili || {}) },
            formazione: { ...DEFAULTS.formazione, ...(letti.formazione || {}) },
            dpi: { ...DEFAULTS.dpi, ...(letti.dpi || {}) },
            ponteggio: { ...DEFAULTS.ponteggio, ...(letti.ponteggio || {}) },
            attrezzature: { ...DEFAULTS.attrezzature, ...(letti.attrezzature || {}) },
            documenti: { ...DEFAULTS.documenti, ...(letti.documenti || {}) },
            completeSezioni: letti.completeSezioni || {},
          });
        } else {
          // Prima apertura: usa defaults dal POS Poderino
          setDati({
            ...DEFAULTS,
            completeSezioni: {},
          });
        }
      } catch (err) {
        console.error(err);
        setErrore("Errore caricamento: " + err.message);
        setDati({ ...DEFAULTS, completeSezioni: {} });
      } finally {
        setLoading(false);
      }
    }
    carica();
  }, []);

  // SALVATAGGIO SEZIONE
  async function salvaSezione(sezioneId) {
    setSalvandoSezione(sezioneId);
    setErrore(null);
    try {
      const ref = doc(db, 'impostazioni', 'sicurezza_azienda');
      const payload = {
        impresa: dati.impresa,
        responsabili: dati.responsabili,
        formazione: dati.formazione,
        dpi: dati.dpi,
        ponteggio: dati.ponteggio,
        attrezzature: dati.attrezzature,
        documenti: dati.documenti,
        completeSezioni: {
          ...dati.completeSezioni,
          [sezioneId]: true,
        },
        updatedAt: new Date().toISOString(),
      };
      await setDoc(ref, payload, { merge: false });
      setDati({ ...dati, completeSezioni: payload.completeSezioni });
      setUltimoSalvataggio({ ...ultimoSalvataggio, [sezioneId]: Date.now() });
      // chiudi sezione dopo salvataggio
      setTimeout(() => setSezioneAperta(null), 400);
    } catch (err) {
      console.error(err);
      setErrore("Errore salvataggio: " + err.message);
    } finally {
      setSalvandoSezione(null);
    }
  }

  if (loading) {
    return (
      <div className="content" style={{ textAlign: "center", padding: "60px 20px" }}>
        <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 22, color: theme.accent }}>
          🛡️ Sicurezza Azienda
        </div>
        <div style={{ color: theme.textMuted, marginTop: 20 }}>Caricamento...</div>
      </div>
    );
  }

  const sezioni = [
    { id: "impresa", num: 1, titolo: "Dati impresa", sottotitolo: "Ragione sociale, P.IVA, email, PEC, telefono" },
    { id: "responsabili", num: 2, titolo: "Responsabili sicurezza", sottotitolo: "RSPP, primo soccorso, antincendio, RLS, medico" },
    { id: "lavoratori", num: 3, titolo: "Lavoratori", sottotitolo: "Anagrafica e formazione di ciascun operaio" },
    { id: "ponteggio", num: 4, titolo: "Ponteggio aziendale", sottotitolo: "Marca, modello, numero autorizzazione ministeriale" },
    { id: "documenti", num: 5, titolo: "Documenti aziendali", sottotitolo: "DURC, visura, INAIL, INPS (scadenze)" },
    { id: "schede", num: 6, titolo: "Schede di Sicurezza C&F", sottotitolo: "Schede di sicurezza per le lavorazioni del cantiere" },
    { id: "macchine", num: 7, titolo: "Macchine e attrezzature", sottotitolo: "Elenco macchine e attrezzature del cantiere" },
    { id: "modalita", num: 8, titolo: "Modalità organizzative cantiere", sottotitolo: "Recinzione, viabilità, depositi, servizi" },
    { id: "rifiuti", num: 9, titolo: "Gestione rifiuti", sottotitolo: "Tipologia, documentazione e misure rifiuti" },
  ];

  const nSezioniComplete = sezioni.filter(s => dati.completeSezioni[s.id]).length;

  return (
    <div className="content" style={{ maxWidth: 900, margin: "0 auto", padding: "0 8px" }}>

      {/* HEADER */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, paddingBottom: 16, borderBottom: `1px solid ${theme.border}` }}>
        <div>
          <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 24, color: theme.accent }}>
            🛡️ Sicurezza Azienda
          </div>
          <div style={{ fontSize: 13, color: theme.textMuted, marginTop: 4 }}>
            Dati fissi usati per generare il POS. Compila una sezione alla volta.
          </div>
        </div>
        <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 8, padding: "8px 14px", fontSize: 13, color: theme.textMuted, fontWeight: 600 }}>
          {nSezioniComplete}/7 complete
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 12, padding: "12px 16px", marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: theme.textMuted }}>Visura, DURC, attestati… scegli i documenti e manda un link che scade da solo.</div>
        <InviaDocumentiBtn />
      </div>

      {errore && (
        <div style={{ background: "#2a0f12", border: `1px solid ${theme.error}44`, borderRadius: 10, padding: "12px 16px", marginBottom: 16, fontSize: 13, color: theme.error }}>
          ⚠️ {errore}
        </div>
      )}

      {/* SEZIONI */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {sezioni.map(s => (
          <SezioneAccordion
            key={s.id}
            sezione={s}
            completa={!!dati.completeSezioni[s.id]}
            aperta={sezioneAperta === s.id}
            onToggle={() => setSezioneAperta(sezioneAperta === s.id ? null : s.id)}
            salvando={salvandoSezione === s.id}
            salvataggioOk={ultimoSalvataggio[s.id] && (Date.now() - ultimoSalvataggio[s.id] < 3000)}
            onSalva={() => salvaSezione(s.id)}
            dati={dati}
            setDati={setDati}
            onNavigate={onNavigate}
          />
        ))}
      </div>

      {onIndietro && (
        <button onClick={onIndietro} style={{ ...btnSecondarioSt, marginTop: 20, width: "100%" }}>
          ← Torna al menu
        </button>
      )}
    </div>
  );
}

// ============================================================================
// COMPONENTE SEZIONE ACCORDION
// ============================================================================
function SezioneAccordion({ sezione, completa, aperta, onToggle, salvando, salvataggioOk, onSalva, dati, setDati, onNavigate }) {

  const stato = completa ? "complete" : (aperta ? "aperta" : "vuota");
  const coloreAccento = stato === "complete" ? theme.success : (stato === "aperta" ? theme.info : "#555");
  const coloreBadgeBg = stato === "complete" ? "#0a2218" : (stato === "aperta" ? "#0c2a4d" : theme.bg);
  const coloreBadgeText = stato === "complete" ? theme.success : (stato === "aperta" ? theme.info : theme.textMuted);
  const testoBadge = stato === "complete" ? "Completa" : (stato === "aperta" ? "In modifica" : "Da compilare");

  return (
    <div style={{
      background: theme.surface,
      border: `1px solid ${theme.border}`,
      borderRadius: 12,
      borderLeft: `4px solid ${coloreAccento}`,
      overflow: "hidden",
    }}>
      {/* HEADER CLICCABILE */}
      <div
        onClick={onToggle}
        style={{
          padding: "14px 18px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 28,
            height: 28,
            borderRadius: "50%",
            background: coloreBadgeBg,
            color: coloreBadgeText,
            fontSize: 14,
            fontWeight: 700,
          }}>
            {sezione.num}
          </span>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: theme.text }}>{sezione.titolo}</div>
            <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 2 }}>{sezione.sottotitolo}</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {salvataggioOk && (
            <span style={{ fontSize: 12, color: theme.success }}>✓ salvato</span>
          )}
          <span style={{
            fontSize: 11,
            color: coloreBadgeText,
            background: coloreBadgeBg,
            padding: "4px 10px",
            borderRadius: 6,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}>
            {testoBadge}
          </span>
          <span style={{ color: theme.textDim, fontSize: 12 }}>
            {aperta ? "▲" : "▼"}
          </span>
        </div>
      </div>

      {/* CORPO */}
      {aperta && (
        <div style={{ padding: "4px 18px 18px", borderTop: `1px solid ${theme.border}` }}>
          {sezione.id === "impresa"      && <FormImpresa      dati={dati} setDati={setDati} />}
          {sezione.id === "responsabili" && <FormResponsabili dati={dati} setDati={setDati} />}
          {sezione.id === "lavoratori"   && <FormLavoratori />}
          {sezione.id === "ponteggio"    && <FormPonteggio    dati={dati} setDati={setDati} />}
          {sezione.id === "documenti"    && <DocumentiAziendaBox />}
      {sezione.id === "schede"       && <SchedeCFBox />}
      {sezione.id === "macchine"     && <MacchineBox />} {/* FIX_MACCHINE_IN_SICUREZZA_v1 */}
      {sezione.id === "modalita"     && <ModalitaBox />} {/* FIX_MODALITA_IN_SICUREZZA_v1 */}
      {sezione.id === "rifiuti"      && <RifiutiBox />} {/* FIX_RIFIUTI_IN_SICUREZZA_v1 */}

          <div style={{ display: "flex", gap: 10, marginTop: 18, justifyContent: "flex-end" }}>
            <button onClick={onToggle} disabled={salvando} style={btnSecondarioSt}>
              Chiudi
            </button>
            <button onClick={onSalva} disabled={salvando} style={{ ...btnPrimarioSt, opacity: salvando ? 0.6 : 1 }}>
              {salvando ? "Salvataggio..." : "✓ Salva sezione"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// FORM SEZIONE 1: IMPRESA
// ============================================================================
function FormImpresa({ dati, setDati }) {
  const imp = dati.impresa;
  const aggiorna = (campo, val) => setDati({ ...dati, impresa: { ...imp, [campo]: val } });

  return (
    <div style={{ padding: "14px 0" }}>
      <Griglia2col>
        <Campo label="Ragione sociale" value={imp.ragioneSociale} onChange={v => aggiorna("ragioneSociale", v)} />
        <Campo label="P.IVA / Codice Fiscale" value={imp.pIva} onChange={v => aggiorna("pIva", v)} />
      </Griglia2col>
      <div style={{ marginTop: 12 }}>
        <Campo label="Sede legale (via + numero)" value={imp.sedeLegale} onChange={v => aggiorna("sedeLegale", v)} />
      </div>
      <Griglia3col>
        <Campo label="Città" value={imp.sedeCitta} onChange={v => aggiorna("sedeCitta", v)} />
        <Campo label="CAP" value={imp.sedeCap} onChange={v => aggiorna("sedeCap", v)} />
        <Campo label="Provincia" value={imp.sedeProvincia} onChange={v => aggiorna("sedeProvincia", v)} />
      </Griglia3col>
      <Griglia2col>
        <Campo label="Email" value={imp.email} onChange={v => aggiorna("email", v)} />
        <Campo label="PEC" value={imp.pec} onChange={v => aggiorna("pec", v)} />
      </Griglia2col>
      <Griglia2col>
        <Campo label="Telefono" value={imp.telefono} onChange={v => aggiorna("telefono", v)} />
        <Campo label="Attività dell'impresa" value={imp.attivita} onChange={v => aggiorna("attivita", v)} />
      </Griglia2col>
      <div style={{ marginTop: 12 }}>
        <Campo label="Datore di lavoro" value={imp.datoreDiLavoro} onChange={v => aggiorna("datoreDiLavoro", v)} />
      </div>
      <Griglia3col>
        <Campo label="REA" value={imp.rea} onChange={v => aggiorna("rea", v)} />
        <Campo label="AIA" value={imp.aia} onChange={v => aggiorna("aia", v)} />
        <Campo label="INAIL" value={imp.inail} onChange={v => aggiorna("inail", v)} />
      </Griglia3col>
    </div>
  );
}

// ============================================================================
// FORM SEZIONE 2: RESPONSABILI
// ============================================================================
function FormResponsabili({ dati, setDati }) {
  const r = dati.responsabili;
  const aggiorna = (campo, val) => setDati({ ...dati, responsabili: { ...r, [campo]: val } });

  return (
    <div style={{ padding: "14px 0" }}>
      <Griglia2col>
        <Campo label="RSPP (Resp. Servizio Prevenzione)" value={r.rspp} onChange={v => aggiorna("rspp", v)} />
        <Campo label="Addetto Primo Soccorso" value={r.primoSoccorso} onChange={v => aggiorna("primoSoccorso", v)} />
      </Griglia2col>
      <Griglia2col>
        <Campo label="Addetto Antincendio e Evacuazione" value={r.antincendio} onChange={v => aggiorna("antincendio", v)} />
        <Campo label="Medico Competente" value={r.medicoCompetente} onChange={v => aggiorna("medicoCompetente", v)} />
      </Griglia2col>
      <Griglia2col>
        <div style={{ marginTop: 12 }}>
          <div style={labelSt}>Tipo RLS</div>
          <select style={inputSt} value={r.rlsTipo} onChange={e => aggiorna("rlsTipo", e.target.value)}>
            <option value="aziendale">Aziendale (RLS)</option>
            <option value="territoriale">Territoriale (RLST)</option>
            <option value="sito">Di sito produttivo</option>
          </select>
        </div>
        <Campo label="Nominativo RLS" value={r.rls} onChange={v => aggiorna("rls", v)} />
      </Griglia2col>
      <div style={{ marginTop: 12 }}>
        <Campo label="Direttore Tecnico di cantiere (opz.)" value={r.direttoreTecnico} onChange={v => aggiorna("direttoreTecnico", v)} />
      </div>
    </div>
  );
}

// ============================================================================
// FORM SEZIONE 3: LAVORATORI (lista + modal anagrafica/formazione)  [STEP F3b.1]
// ============================================================================

// Etichette colonne formazione (10 fisse, ordine del POS)
const FORMAZIONE_FIELDS = [
  { key: "corso_base",        label: "Corso base (art.37)" },
  { key: "agg_corso_base",    label: "Aggiornamento corso base (art.37)" },
  { key: "rspp",              label: "RSPP Datori di lavoro (art.34)" },
  { key: "primo_soccorso",    label: "Addetto primo soccorso" },
  { key: "antincendio",       label: "Addetto antincendio evacuazione" },
  { key: "ple",               label: "PLE - Piattaforme (art.73)" },
  { key: "ponteggi_pimus",    label: "Ponteggi (art.136)" },
  { key: "agg_ponteggi",      label: "Aggiornamento ponteggi (art.136)" },
  { key: "lavori_in_quota",   label: "Lavori in quota (art.116)" },
  { key: "rls",               label: "RLS (art.37)" },
];

// Mappa la chiave UI alle chiavi reali su Firestore: alcune sono dirette, altre sono OR di piu' campi
function leggiFlagFormazione(formazione, key) {
  const f = formazione || {};
  const has = v => v && (typeof v === "object" ? Object.keys(v).length > 0 : !!v);
  switch (key) {
    case "corso_base":      return has(f.generale_specifica) || has(f.generale_specifica_2015) || has(f.generale) || has(f.specifica) || has(f.corso);
    case "agg_corso_base":  return has(f.aggiornamento_lavoratori);
    case "rspp":            return has(f.rspp);
    case "primo_soccorso":  return has(f.primo_soccorso);
    case "antincendio":     return has(f.antincendio);
    case "ple":             return has(f.ple);
    case "ponteggi_pimus":  return has(f.ponteggi_pimus);
    case "agg_ponteggi":    return has(f.aggiornamento_ponteggi);
    case "lavori_in_quota": return has(f.lavori_in_quota);
    case "rls":             return has(f.rls);
    default: return false;
  }
}

// Imposta/resetta una colonna formazione (semplice: scrive { fatto: true } o rimuove).
function impostaFlagFormazione(formazione, key, value) {
  const f = { ...(formazione || {}) };
  // mappatura chiave UI -> chiave Firestore principale (per scrittura)
  const writeMap = {
    corso_base: "generale_specifica",
    agg_corso_base: "aggiornamento_lavoratori",
    rspp: "rspp",
    primo_soccorso: "primo_soccorso",
    antincendio: "antincendio",
    ple: "ple",
    ponteggi_pimus: "ponteggi_pimus",
    agg_ponteggi: "aggiornamento_ponteggi",
    lavori_in_quota: "lavori_in_quota",
    rls: "rls",
  };
  const fsKey = writeMap[key];
  if (!fsKey) return f;
  if (value) {
    if (!f[fsKey] || typeof f[fsKey] !== "object") {
      f[fsKey] = { fatto: true };
    } else if (Object.keys(f[fsKey]).length === 0) {
      f[fsKey] = { fatto: true };
    }
    // se esisteva con valore, lo lascio com'e'
  } else {
    // rimuovo TUTTE le chiavi che concorrono al flag (per "corso_base" sono diverse)
    if (key === "corso_base") {
      delete f.generale_specifica;
      delete f.generale_specifica_2015;
      delete f.generale;
      delete f.specifica;
      delete f.corso;
    } else {
      delete f[fsKey];
    }
  }
  return f;
}

function authHeaders() {
  const token = (typeof window !== "undefined" && window.localStorage)
    ? window.localStorage.getItem("bertoc_jwt")
    : null;
  const h = { "Content-Type": "application/json" };
  if (token) h["Authorization"] = "Bearer " + token;
  return h;
}


const DPI_DEFAULT_LIST = [
  "Casco di protezione",
  "Scarpe antinfortunistica con suola imperforabile",
  "Guanti di protezione",
  "Occhiali protettivi",
  "Mascherina FFP2",
  "Cuffie antirumore",
  "Gilet alta visibilita'",
  "Imbragatura anticaduta",
];
const VISITA_TIPI = ["Preventiva", "Periodica", "Aggiornamento", "Cambio mansione", "Altro"];
const VISITA_IDONEITA = ["Idoneo", "Idoneo con prescrizioni", "Non idoneo temporaneamente", "Non idoneo"];


// Categorie documenti per lavoratore (matchano i tipi accettati dal backend)
const TIPI_DOC_LAVORATORE = [
  { id: "formazione", label: "Attestati formazione", emoji: "📚" },
  { id: "idoneita",   label: "Idoneita sanitaria",   emoji: "🏥" },
  { id: "identita",   label: "Documento identita",   emoji: "🪪" },
  { id: "altro",      label: "Altro",                emoji: "📎" },
];


function FormLavoratori() {
  const [lavoratori, setLavoratori] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [errLista, setErrLista] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null o oggetto lavoratore (con id se esistente)

  async function caricaLista() {
    setLoadingList(true);
    setErrLista(null);
    try {
      const r = await fetch("/api/lavoratori", { headers: authHeaders() });
      if (!r.ok) throw new Error("HTTP " + r.status);
      const j = await r.json();
      if (!j.ok) throw new Error(j.error || "Errore");
      // ordino per cognome
      const lista = (j.lavoratori || []).slice().sort((a, b) => {
        return (a.cognome || "").localeCompare(b.cognome || "");
      });
      setLavoratori(lista);
    } catch (err) {
      console.error("[LAV] Lista:", err);
      setErrLista(err.message);
    } finally {
      setLoadingList(false);
    }
  }

  useEffect(() => { caricaLista(); }, []);

  function apriNuovo() {
    setEditing({
      id: null,
      nome: "",
      cognome: "",
      codice_fiscale: "",
      data_nascita: "",
      cittadinanza: "",
      attivo: true,
      mansione_contratto: "",
      mansione_specifica: "",
      ruolo_sicurezza: "",
      formazione: {},
    });
    setModalOpen(true);
  }

  function apriEdit(lav) {
    setEditing({ ...lav });
    setModalOpen(true);
  }

  async function salva(lav) {
    try {
      const isNew = !lav.id;
      const url = isNew ? "/api/lavoratori" : "/api/lavoratori/" + lav.id;
      const method = isNew ? "POST" : "PUT";
      const r = await fetch(url, {
        method: method,
        headers: authHeaders(),
        body: JSON.stringify(lav),
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error("HTTP " + r.status + ": " + t.slice(0, 100));
      }
      setModalOpen(false);
      setEditing(null);
      await caricaLista();
    } catch (err) {
      alert("Errore salvataggio: " + err.message);
    }
  }

  async function elimina(lav) {
    if (!lav.id) return;
    try {
      // STEP F5b: prima sposta cartella documenti nel cestino filesystem (se esiste)
      try {
        await fetch("/api/lavoratori/" + lav.id + "/cartella", {
          method: "DELETE",
          headers: authHeaders(),
        });
      } catch (e) {
        console.warn("[LAV] cestino cartella fallito:", e);
      }
      // poi elimina dal Firestore (cestino logico)
      const r = await fetch("/api/lavoratori/" + lav.id, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error("HTTP " + r.status + ": " + t.slice(0, 100));
      }
      setModalOpen(false);
      setEditing(null);
      await caricaLista();
    } catch (err) {
      alert("Errore eliminazione: " + err.message);
    }
  }

  if (loadingList) {
    return <div style={{ padding: "20px", textAlign: "center", color: theme.textMuted }}>Caricamento...</div>;
  }
  if (errLista) {
    return <div style={{ padding: "20px", color: theme.error }}>Errore: {errLista}</div>;
  }

  return (
    <div style={{ padding: "14px 0" }}>
      <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 12 }}>
        I dati anagrafici e formazione di ogni lavoratore vengono salvati automaticamente alla chiusura del modal. Click su una riga per modificare.
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
        <button
          onClick={apriNuovo}
          style={{ ...btnPrimarioSt, fontSize: 13, padding: "8px 14px" }}
        >+ Nuovo lavoratore</button>
      </div>

      <div style={{ border: `1px solid ${theme.border}`, borderRadius: 8, overflow: "hidden" }}>
        {lavoratori.length === 0 && (
          <div style={{ padding: "30px 20px", textAlign: "center", color: theme.textMuted }}>
            Nessun lavoratore. Aggiungi il primo.
          </div>
        )}
        {lavoratori.map((l, idx) => {
          const flagsAttivi = FORMAZIONE_FIELDS.filter(f => leggiFlagFormazione(l.formazione, f.key)).length;
          return (
            <div
              key={l.id || idx}
              onClick={() => apriEdit(l)}
              style={{
                padding: "12px 14px",
                borderBottom: idx < lavoratori.length - 1 ? `1px solid ${theme.border}` : "none",
                cursor: "pointer",
                background: theme.surface,
                transition: "background 0.15s",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
              onMouseEnter={e => e.currentTarget.style.background = theme.surfaceHover}
              onMouseLeave={e => e.currentTarget.style.background = theme.surface}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: theme.text }}>
                  {l.nome_completo || ((l.nome || "") + " " + (l.cognome || "")).trim() || "(senza nome)"}
                  {!l.attivo && <span style={{ marginLeft: 8, fontSize: 10, color: theme.warning, fontWeight: 500 }}>NON ATTIVO</span>}
                </div>
                <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 2 }}>
                  {l.mansione_specifica || l.mansione_contratto || "—"}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{
                  fontSize: 11,
                  color: flagsAttivi > 0 ? theme.accent : theme.textDim,
                  fontWeight: 600,
                  padding: "3px 8px",
                  border: `1px solid ${flagsAttivi > 0 ? theme.accent : theme.border}`,
                  borderRadius: 12,
                }}>
                  {flagsAttivi}/{FORMAZIONE_FIELDS.length} corsi
                </div>
                <div style={{ fontSize: 18, color: theme.textDim }}>›</div>
              </div>
            </div>
          );
        })}
      </div>

      {modalOpen && editing && (
        <ModalLavoratore
          dati={editing}
          onChange={setEditing}
          onSalva={() => salva(editing)}
          onAnnulla={() => { setModalOpen(false); setEditing(null); }}
          onElimina={() => elimina(editing)}
        />
      )}
    </div>
  );
}

function ModalLavoratore({ dati, onChange, onSalva, onAnnulla, onElimina }) {

function DocumentiLavoratoreBox({ lavoratoreId }) {
  const [files, setFiles] = useState({ formazione: [], idoneita: [], identita: [], altro: [] });
  const [archivio, setArchivio] = useState({ formazione: [], idoneita: [], identita: [], altro: [] });
  const [archivioCount, setArchivioCount] = useState({});
  const [showArchivio, setShowArchivio] = useState({}); // { formazione: true, ... }
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const [errore, setErrore] = useState(null);

  async function caricaFiles(includeArc) {
    setLoading(true);
    setErrore(null);
    try {
      const url = "/api/lavoratori/" + lavoratoreId + "/files" + (includeArc ? "?include_archivio=true" : "");
      const r = await fetch(url, { headers: authHeaders() });
      if (!r.ok) throw new Error("HTTP " + r.status);
      const j = await r.json();
      if (!j.ok) throw new Error(j.error || "Errore");
      setFiles(j.files || {});
      if (j.archivio) setArchivio(j.archivio);
      setArchivioCount(j.archivio_count || {});
    } catch (err) {
      setErrore(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { caricaFiles(false); }, [lavoratoreId]);

  // se almeno una categoria ha archivio espanso, ricarico con include_archivio=true
  useEffect(() => {
    const anyOpen = Object.values(showArchivio).some(v => v);
    if (anyOpen) caricaFiles(true);
  }, [showArchivio]);

  async function handleUpload(tipo, file) {
    if (!file) return;
    if (file.type && file.type !== "application/pdf") {
      alert("Seleziona un file PDF");
      return;
    }
    setUploading(tipo);
    try {
      const token = (typeof window !== "undefined" && window.localStorage)
        ? window.localStorage.getItem("bertoc_jwt") : null;
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tipo", tipo);
      const r = await fetch("/api/lavoratori/" + lavoratoreId + "/upload", {
        method: "POST",
        headers: { "Authorization": "Bearer " + token },
        body: fd,
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || ("HTTP " + r.status));
      }
      const uploadResp = await r.json().catch(() => ({}));
      await caricaFiles(Object.values(showArchivio).some(v => v));

      // F5c: analisi AI in background (non blocca UI)
      const fnAnalizzato = uploadResp.filename || file.name;
      analizzaConAI("lavoratore", tipo, fnAnalizzato, lavoratoreId).then(() => {
        // refresh files dopo AI per vedere scadenza aggiornata
        caricaFiles(Object.values(showArchivio).some(v => v));
      }).catch(() => { /* errori gia' alertati da analizzaConAI */ });

    } catch (err) {
      alert("Upload fallito: " + err.message);
    } finally {
      setUploading(null);
    }
  }

  // F5c: helper analisi AI - mostra toast quando finito
  async function analizzaConAI(categoria, subTipo, filename, lavId) {
    try {
      const token = (typeof window !== "undefined" && window.localStorage)
        ? window.localStorage.getItem("bertoc_jwt") : null;
      const r = await fetch("/api/documenti/analizza-meta", {
        method: "POST",
        headers: { "Authorization": "Bearer " + token, "Content-Type": "application/json" },
        body: JSON.stringify({ categoria, subTipo, filename, lavId }),
      });
      const j = await r.json();
      if (!r.ok || !j.ok) throw new Error(j.error || ("HTTP " + r.status));
      const d = j.dati || {};
      const parts = [];
      if (d.tipoDocumento) parts.push(d.tipoDocumento);
      if (d.dataScadenza) parts.push("scadenza: " + d.dataScadenza);
      if (d.ente) parts.push("ente: " + d.ente);
      if (d.ore) parts.push("ore: " + d.ore);
      const msg = "AI: " + (parts.length > 0 ? parts.join(" | ") : "nessun dato estratto");
      console.log("[F5c]", filename, msg);
      // toast minimale = alert (semplice, robusto)
      alert("✨ " + filename + "\n" + msg);
    } catch (e) {
      console.warn("[F5c] errore analisi AI:", e.message);
      // non alerto errori AI (silenzioso, l'utente puo' compilare a mano)
    }
  }

  async function handleApri(tipo, filename, daArchivio) {
    try {
      const token = (typeof window !== "undefined" && window.localStorage)
        ? window.localStorage.getItem("bertoc_jwt") : null;
      const baseUrl = daArchivio
        ? "/api/lavoratori/" + lavoratoreId + "/file/archivio"
        : "/api/lavoratori/" + lavoratoreId + "/file";
      /* APRI_SAFARI_LAV_v1: URL diretto col token, i blob non si aprono in Safari */
      const url = baseUrl + "?tipo=" + encodeURIComponent(tipo)
                + "&filename=" + encodeURIComponent(filename);
      const w = window.open("", "_blank"); /* PASS_FILE_v1: pass breve, valido 5 minuti solo per questo file */
      try {
        const rp = await fetch("/api/pass-file", { method: "POST", headers: { "Content-Type": "application/json", "Authorization": "Bearer " + (token || "") }, body: JSON.stringify({ url: url }) });
        const jp = await rp.json();
        if (!rp.ok) throw new Error(jp.error || ("HTTP " + rp.status));
        if (w) { w.opener = null; w.location.href = jp.url; } else { window.location.href = jp.url; }
      } catch (e2) { if (w) w.close(); throw e2; }
      setTimeout(() => { if (!w || w.closed) console.warn("Apertura PDF non riuscita", url); }, 1200);
    } catch (err) {
      alert("Apertura fallita: " + err.message);
    }
  }

  async function handleScarica(tipo, filename, daArchivio) {
    try {
      const token = (typeof window !== "undefined" && window.localStorage)
        ? window.localStorage.getItem("bertoc_jwt") : null;
      const baseUrl = daArchivio
        ? "/api/lavoratori/" + lavoratoreId + "/file/archivio"
        : "/api/lavoratori/" + lavoratoreId + "/file";
      const url = baseUrl + "?tipo=" + encodeURIComponent(tipo) + "&filename=" + encodeURIComponent(filename);
      const r = await fetch(url, { headers: { "Authorization": "Bearer " + token } });
      if (!r.ok) throw new Error("HTTP " + r.status);
      const blob = await r.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (err) {
      alert("Download fallito: " + err.message);
    }
  }

  function handleMail(tipo, filename) {
    const oggetto = "Documento: " + filename;
    const corpo = "Salve,\n\nin allegato trasmettiamo il documento richiesto.\n\nCordiali saluti,\nBERTOC PROJECT SRL";
    window.location.href = "mailto:?subject=" + encodeURIComponent(oggetto) + "&body=" + encodeURIComponent(corpo);
  }

  async function handleElimina(tipo, filename, daArchivio) {
    const conf = daArchivio
      ? "Eliminare DEFINITIVAMENTE '" + filename + "' dall'archivio?"
      : "Eliminare '" + filename + "'?";
    if (!window.confirm(conf)) return;
    try {
      const baseUrl = daArchivio
        ? "/api/lavoratori/" + lavoratoreId + "/file/archivio"
        : "/api/lavoratori/" + lavoratoreId + "/file";
      const url = baseUrl + "?tipo=" + encodeURIComponent(tipo) + "&filename=" + encodeURIComponent(filename);
      const r = await fetch(url, { method: "DELETE", headers: authHeaders() });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || ("HTTP " + r.status));
      }
      await caricaFiles(Object.values(showArchivio).some(v => v));
    } catch (err) {
      alert("Eliminazione fallita: " + err.message);
    }
  }

  async function patchMetaFile(tipo, filename, patch) {
    try {
      const r = await fetch("/api/lavoratori/" + lavoratoreId + "/file/meta", {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ tipo: tipo, filename: filename, ...patch }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || ("HTTP " + r.status));
      }
      await caricaFiles(Object.values(showArchivio).some(v => v));
    } catch (err) {
      alert("Aggiornamento fallito: " + err.message);
    }
  }

  async function handleArchivia(tipo, filename) {
    if (!window.confirm("Spostare '" + filename + "' nell'archivio?")) return;
    try {
      const r = await fetch("/api/lavoratori/" + lavoratoreId + "/file/archivia", {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ tipo: tipo, filename: filename, motivo: "manuale" }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || ("HTTP " + r.status));
      }
      await caricaFiles(Object.values(showArchivio).some(v => v));
    } catch (err) {
      alert("Archiviazione fallita: " + err.message);
    }
  }

  async function handleRipristina(tipo, archivedFilename) {
    try {
      const r = await fetch("/api/lavoratori/" + lavoratoreId + "/file/ripristina", {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ tipo: tipo, filename: archivedFilename }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || ("HTTP " + r.status));
      }
      await caricaFiles(true);
    } catch (err) {
      alert("Ripristino fallito: " + err.message);
    }
  }

  if (loading) return <div style={{ fontSize: 12, color: theme.textMuted, padding: "10px 0" }}>Caricamento...</div>;
  if (errore) return <div style={{ fontSize: 12, color: theme.error, padding: "10px 0" }}>Errore: {errore}</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 6 }}>
      {TIPI_DOC_LAVORATORE.map(cat => {
        const lista = files[cat.id] || [];
        const arcLista = archivio[cat.id] || [];
        const arcN = arcLista.length || archivioCount[cat.id] || 0;
        const isUploading = uploading === cat.id;
        const arcOpen = !!showArchivio[cat.id];
        return (
          <div key={cat.id} style={{
            border: "1px solid " + theme.border,
            borderRadius: 8,
            padding: "10px 12px",
            background: theme.surface,
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <div style={{ fontSize: 12, color: theme.text, fontWeight: 600 }}>
                {cat.emoji} {cat.label} <span style={{ color: theme.textMuted, fontWeight: 400 }}>({lista.length})</span>
              </div>
              <label style={{
                fontSize: 11,
                color: theme.accent,
                cursor: isUploading ? "wait" : "pointer",
                fontWeight: 600,
                padding: "4px 10px",
                border: "1px solid " + theme.accent + "55",
                borderRadius: 6,
              }}>
                {isUploading ? "Caricamento..." : "+ Carica PDF"}
                <input
                  type="file"
                  accept="application/pdf,.pdf"
                  style={{ display: "none" }}
                  disabled={isUploading}
                  onChange={e => {
                    const f = e.target.files && e.target.files[0];
                    if (f) handleUpload(cat.id, f);
                    e.target.value = "";
                  }}
                />
              </label>
            </div>
            {lista.length === 0 && (
              <div style={{ fontSize: 11, color: theme.textDim, fontStyle: "italic" }}>nessun file attivo</div>
            )}
            {lista.map(item => (
              <FileRow
                key={item.filename}
                item={item}
                onApri={() => handleApri(cat.id, item.filename, false)}
                onElimina={() => handleElimina(cat.id, item.filename, false)}
                onPatch={(patch) => patchMetaFile(cat.id, item.filename, patch)}
                onArchivia={() => handleArchivia(cat.id, item.filename)}
              />
            ))}

            {/* ARCHIVIO toggle */}
            {arcN > 0 && (
              <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px dashed " + theme.border }}>
                <button
                  onClick={() => setShowArchivio(s => ({ ...s, [cat.id]: !s[cat.id] }))}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: theme.textMuted,
                    cursor: "pointer",
                    fontSize: 11,
                    fontWeight: 600,
                    padding: 0,
                  }}
                >
                  {arcOpen ? "▾" : "▸"} 📦 Archivio ({arcN})
                </button>
                {arcOpen && (
                  <div style={{ marginTop: 6, opacity: 0.65 }}>
                    {arcLista.length === 0 ? (
                      <div style={{ fontSize: 11, color: theme.textDim, fontStyle: "italic" }}>caricamento archivio...</div>
                    ) : arcLista.map(item => (
                      <FileRowArchivio
                        key={item.filename}
                        item={item}
                        onApri={() => handleApri(cat.id, item.filename, true)}
                        onElimina={() => handleElimina(cat.id, item.filename, true)}
                        onRipristina={() => handleRipristina(cat.id, item.filename)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const STATO_LABEL = {
  ok:           { lbl: "OK",           bg: "#1b4332", color: "#3ecf8e" },
  in_scadenza:  { lbl: "in scadenza",  bg: "#3f3500", color: "#f5c518" },
  scaduto:      { lbl: "SCADUTO",      bg: "#3f0f0f", color: "#ff6b6b" },
  nessuna_data: { lbl: "no data",      bg: "transparent", color: theme.textDim },
};

function FileRow({ item, onApri, onElimina, onPatch, onArchivia }) {
  const [editingScad, setEditingScad] = useState(false);
  const [scadDraft, setScadDraft] = useState(item.scadenza || "");

  function toDateInputValue(s) {
    if (!s) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const m = String(s).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (m) return m[3] + "-" + m[2].padStart(2, "0") + "-" + m[1].padStart(2, "0");
    return "";
  }

  const stato = STATO_LABEL[item.stato] || STATO_LABEL.nessuna_data;
  const giorni = item.giorni_rimanenti;

  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "6px 0", fontSize: 12, color: theme.text,
      borderBottom: "1px solid " + theme.border + "33",
    }}>
      <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
            📄 {item.filename}
          </span>
          {item.nascosto_qr && <span title="Nascosto da QR pubblico" style={{ fontSize: 11 }}>🔒</span>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 3, fontSize: 10, flexWrap: "wrap" }}>
          <span style={{
            padding: "1px 8px",
            borderRadius: 10,
            background: stato.bg,
            color: stato.color,
            fontWeight: 600,
          }}>
            {stato.lbl}{giorni != null && (giorni >= 0 ? " · " + giorni + "gg" : " · " + Math.abs(giorni) + "gg fa")}
          </span>
          {!editingScad ? (
            <button
              onClick={() => { setScadDraft(toDateInputValue(item.scadenza)); setEditingScad(true); }}
              style={{ background: "transparent", border: "none", color: theme.textMuted, cursor: "pointer", fontSize: 10, padding: 0, textDecoration: "underline" }}
            >{item.scadenza ? "modifica" : "+ scadenza"}</button>
          ) : (
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <input
                type="date"
                value={scadDraft}
                onChange={e => setScadDraft(e.target.value)}
                style={{ ...inputSt, padding: "2px 6px", fontSize: 11, width: 130 }}
                autoFocus
              />
              <button
                onClick={async () => { await onPatch({ scadenza: scadDraft }); setEditingScad(false); }}
                style={{ background: theme.accent, border: "none", color: "#1a1d27", cursor: "pointer", fontSize: 10, padding: "2px 6px", borderRadius: 4, fontWeight: 600 }}
              >✓</button>
              <button
                onClick={() => setEditingScad(false)}
                style={{ background: "transparent", border: "1px solid " + theme.border, color: theme.textMuted, cursor: "pointer", fontSize: 10, padding: "2px 6px", borderRadius: 4 }}
              >✕</button>
            </span>
          )}
          <button
            onClick={() => onPatch({ nascosto_qr: !item.nascosto_qr })}
            title={item.nascosto_qr ? "Visibile a QR pubblico" : "Nascondi da QR pubblico (privacy)"}
            style={{
              background: "transparent", border: "none", cursor: "pointer",
              color: item.nascosto_qr ? theme.warning : theme.textDim,
              fontSize: 11, padding: 0, textDecoration: "underline",
            }}
          >{item.nascosto_qr ? "🔒 nascosto QR" : "mostra QR"}</button>
        </div>
      </div>
      <div style={{ display: "flex", gap: 6, marginLeft: 10 }}>
        <button onClick={onApri} title="Apri PDF" style={{ background: "transparent", border: "none", cursor: "pointer", color: theme.info, fontSize: 13, padding: "2px 6px" }}>👁</button>
        <button onClick={onArchivia} title="Sposta in archivio" style={{ background: "transparent", border: "none", cursor: "pointer", color: theme.textMuted, fontSize: 13, padding: "2px 6px" }}>📦</button>
        <button onClick={onElimina} title="Elimina" style={{ background: "transparent", border: "none", cursor: "pointer", color: theme.error, fontSize: 13, padding: "2px 6px" }}>✕</button>
      </div>
    </div>
  );
}

function FileRowArchivio({ item, onApri, onElimina, onRipristina }) {
  const dataArc = item.archiviato_il ? new Date(item.archiviato_il).toLocaleDateString("it-IT") : "?";
  const motivo = item.motivo_archivio === "scaduto_auto" ? "scaduto auto" : (item.motivo_archivio || "manuale");
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "5px 0", fontSize: 11, color: theme.text,
    }}>
      <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, color: theme.textMuted, textDecoration: "line-through" }}>
            📄 {item.nome_originale || item.filename}
          </span>
        </div>
        <div style={{ fontSize: 10, color: theme.textDim, marginTop: 2 }}>
          archiviato {dataArc} · {motivo}{item.scadenza ? " · scadenza " + item.scadenza : ""}
        </div>
      </div>
      <div style={{ display: "flex", gap: 6, marginLeft: 10 }}>
        <button onClick={onApri} title="Apri PDF" style={{ background: "transparent", border: "none", cursor: "pointer", color: theme.info, fontSize: 12, padding: "2px 6px" }}>👁</button>
        <button onClick={onRipristina} title="Ripristina come attivo" style={{ background: "transparent", border: "none", cursor: "pointer", color: theme.success, fontSize: 12, padding: "2px 6px" }}>↺</button>
        <button onClick={onElimina} title="Elimina definitivamente" style={{ background: "transparent", border: "none", cursor: "pointer", color: theme.error, fontSize: 12, padding: "2px 6px" }}>✕</button>
      </div>
    </div>
  );
}



function PlaceholderDpiSpostato() {
  return (
    <div style={{ padding: "30px 20px", textAlign: "center" }}>
      <div style={{ fontSize: 36, marginBottom: 12 }}>👷</div>
      <div style={{ fontSize: 14, color: theme.text, fontWeight: 600, marginBottom: 8 }}>
        I DPI si gestiscono per ogni lavoratore
      </div>
      <div style={{ fontSize: 12, color: theme.textMuted, lineHeight: 1.6, maxWidth: 420, margin: "0 auto" }}>
        Vai alla <strong style={{ color: theme.accent }}>Sezione 3 - Lavoratori</strong>, apri un lavoratore
        e troverai i DPI dentro al suo profilo personale, insieme alla formazione e alla visita medica.
      </div>
    </div>
  );
}

  const isNew = !dati.id;

  function setField(k, v) { onChange({ ...dati, [k]: v }); }
  function toggleFlag(key) {
    const att = leggiFlagFormazione(dati.formazione, key);
    const nuova = impostaFlagFormazione(dati.formazione, key, !att);
    onChange({ ...dati, formazione: nuova });
  }

  const overlaySt = {
    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
    background: "rgba(0,0,0,0.7)", zIndex: 1000,
    display: "flex", alignItems: "flex-start", justifyContent: "center",
    padding: "30px 12px", overflowY: "auto",
  };
  const modalSt = {
    background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 14,
    padding: "20px 22px", maxWidth: 640, width: "100%",
    fontFamily: "'DM Sans',sans-serif",
  };
  const sectionTitleSt = {
    fontSize: 11, color: theme.textDim, textTransform: "uppercase", letterSpacing: 1,
    fontWeight: 700, marginBottom: 10, marginTop: 18,
  };
  const rowSt = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 };

  return (
    <div style={overlaySt} onClick={onAnnulla}>
      <div style={modalSt} onClick={e => e.stopPropagation()}>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
          <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 18, color: theme.accent }}>
            {isNew ? "Nuovo lavoratore" : "Modifica lavoratore"}
          </div>
          <button onClick={onAnnulla} style={{ background: "transparent", border: "none", color: theme.textMuted, fontSize: 20, cursor: "pointer" }}>×</button>
        </div>

        {/* ANAGRAFICA */}
        <div style={sectionTitleSt}>Anagrafica</div>
        <div style={rowSt}>
          <div>
            <div style={labelSt}>Nome</div>
            <input style={inputSt} value={dati.nome || ""} onChange={e => setField("nome", e.target.value)} />
          </div>
          <div>
            <div style={labelSt}>Cognome</div>
            <input style={inputSt} value={dati.cognome || ""} onChange={e => setField("cognome", e.target.value)} />
          </div>
        </div>
        <div style={rowSt}>
          <div>
            <div style={labelSt}>Codice fiscale</div>
            <input style={inputSt} value={dati.codice_fiscale || ""} onChange={e => setField("codice_fiscale", e.target.value.toUpperCase())} />
          </div>
          <div>
            <div style={labelSt}>Data nascita</div>
            <input style={inputSt} placeholder="gg/mm/aaaa" value={dati.data_nascita || ""} onChange={e => setField("data_nascita", e.target.value)} />
          </div>
        </div>
        <div style={rowSt}>
          <div>
            <div style={labelSt}>Cittadinanza</div>
            <input style={inputSt} value={dati.cittadinanza || ""} onChange={e => setField("cittadinanza", e.target.value)} />
          </div>
          <div>
            <div style={labelSt}>Attivo</div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", cursor: "pointer", color: theme.text, fontSize: 14 }}>
              <input type="checkbox" checked={dati.attivo !== false} onChange={e => setField("attivo", e.target.checked)} style={{ width: 18, height: 18, accentColor: theme.accent }} />
              {dati.attivo !== false ? "Sì, attivo" : "No, non attivo"}
            </label>
          </div>
        </div>

        {/* MANSIONE */}
        <div style={sectionTitleSt}>Mansione</div>
        <div style={{ marginBottom: 10 }}>
          <div style={labelSt}>Mansione contratto</div>
          <input style={inputSt} placeholder="es. Operaio polivalente, Socio lavoratore..." value={dati.mansione_contratto || ""} onChange={e => setField("mansione_contratto", e.target.value)} />
        </div>
        <div style={{ marginBottom: 10 }}>
          <div style={labelSt}>Mansione specifica</div>
          <input style={inputSt} placeholder="es. Capocantiere, Tinteggiatore..." value={dati.mansione_specifica || ""} onChange={e => setField("mansione_specifica", e.target.value)} />
        </div>
        <div style={{ marginBottom: 10 }}>
          <div style={labelSt}>Ruolo sicurezza</div>
          <input style={inputSt} placeholder="es. RLS, Datore di lavoro / RSPP..." value={dati.ruolo_sicurezza || ""} onChange={e => setField("ruolo_sicurezza", e.target.value)} />
        </div>

        {/* FORMAZIONE */}
        <div style={sectionTitleSt}>Formazione</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 6 }}>
          {FORMAZIONE_FIELDS.map(c => {
            const att = leggiFlagFormazione(dati.formazione, c.key);
            return (
              <label
                key={c.key}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "8px 10px", cursor: "pointer",
                  border: `1px solid ${att ? theme.accent : theme.border}`,
                  background: att ? theme.accent + "15" : "transparent",
                  borderRadius: 6, fontSize: 12, color: theme.text,
                }}
              >
                <input
                  type="checkbox"
                  checked={att}
                  onChange={() => toggleFlag(c.key)}
                  style={{ accentColor: theme.accent, cursor: "pointer", width: 16, height: 16 }}
                />
                <span style={{ flex: 1 }}>{c.label}</span>
              </label>
            );
          })}
        </div>

        {/* DPI */}
        <div style={sectionTitleSt}>DPI consegnati</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 10 }}>
          {DPI_DEFAULT_LIST.map(dpiName => {
            const elenco = (dati.dpi && Array.isArray(dati.dpi.elenco)) ? dati.dpi.elenco : [];
            const att = elenco.indexOf(dpiName) !== -1;
            return (
              <label
                key={dpiName}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "8px 10px", cursor: "pointer",
                  border: `1px solid ${att ? theme.accent : theme.border}`,
                  background: att ? theme.accent + "15" : "transparent",
                  borderRadius: 6, fontSize: 12, color: theme.text,
                }}
              >
                <input
                  type="checkbox"
                  checked={att}
                  onChange={() => {
                    const cur = (dati.dpi && Array.isArray(dati.dpi.elenco)) ? dati.dpi.elenco.slice() : [];
                    const idx = cur.indexOf(dpiName);
                    if (idx >= 0) cur.splice(idx, 1); else cur.push(dpiName);
                    onChange({ ...dati, dpi: { ...(dati.dpi || {}), elenco: cur } });
                  }}
                  style={{ accentColor: theme.accent, cursor: "pointer", width: 16, height: 16 }}
                />
                <span style={{ flex: 1 }}>{dpiName}</span>
              </label>
            );
          })}
        </div>
        <div style={{ marginBottom: 10 }}>
          <div style={labelSt}>DPI extra (separati da virgola)</div>
          <input
            style={inputSt}
            placeholder="es. Mascherina FFP3, Tuta protettiva..."
            value={(() => {
              const elenco = (dati.dpi && Array.isArray(dati.dpi.elenco)) ? dati.dpi.elenco : [];
              return elenco.filter(x => DPI_DEFAULT_LIST.indexOf(x) === -1).join(", ");
            })()}
            onChange={e => {
              const elenco = (dati.dpi && Array.isArray(dati.dpi.elenco)) ? dati.dpi.elenco : [];
              const standard = elenco.filter(x => DPI_DEFAULT_LIST.indexOf(x) !== -1);
              const extra = e.target.value.split(",").map(s => s.trim()).filter(Boolean);
              onChange({ ...dati, dpi: { ...(dati.dpi || {}), elenco: standard.concat(extra) } });
            }}
          />
        </div>
        <div style={{ marginBottom: 10 }}>
          <div style={labelSt}>Data consegna DPI</div>
          <input
            style={inputSt}
            placeholder="gg/mm/aaaa"
            value={(dati.dpi && dati.dpi.data_consegna) || ""}
            onChange={e => onChange({ ...dati, dpi: { ...(dati.dpi || {}), data_consegna: e.target.value } })}
          />
        </div>

        {/* VISITA MEDICA */}
        <div style={sectionTitleSt}>Visita medica</div>
        <div style={rowSt}>
          <div>
            <div style={labelSt}>Tipo</div>
            <select
              style={inputSt}
              value={(dati.visita_medica && dati.visita_medica.tipo) || ""}
              onChange={e => onChange({ ...dati, visita_medica: { ...(dati.visita_medica || {}), tipo: e.target.value } })}
            >
              <option value="">- seleziona -</option>
              {VISITA_TIPI.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <div style={labelSt}>Data</div>
            <input
              style={inputSt}
              placeholder="gg/mm/aaaa"
              value={(dati.visita_medica && dati.visita_medica.data) || ""}
              onChange={e => onChange({ ...dati, visita_medica: { ...(dati.visita_medica || {}), data: e.target.value } })}
            />
          </div>
        </div>
        <div style={{ marginBottom: 10 }}>
          <div style={labelSt}>Idoneita</div>
          <select
            style={inputSt}
            value={(dati.visita_medica && dati.visita_medica.idoneita) || ""}
            onChange={e => onChange({ ...dati, visita_medica: { ...(dati.visita_medica || {}), idoneita: e.target.value } })}
          >
            <option value="">- seleziona -</option>
            {VISITA_IDONEITA.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        {/* DOCUMENTI */}
        <div style={sectionTitleSt}>Documenti (PDF)</div>
        {!dati.id && (
          <div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 10, fontStyle: "italic" }}>
            Salva prima il lavoratore per poter caricare documenti.
          </div>
        )}
        {dati.id && (
          <DocumentiLavoratoreBox lavoratoreId={dati.id} />
        )}

        {/* AZIONI */}
        <div style={{ display: "flex", gap: 10, marginTop: 22, justifyContent: "space-between", alignItems: "center" }}>
          <div>
            {!isNew && onElimina && (
              <button
                onClick={() => {
                  var msg = "Eliminare '" + (dati.nome_completo || dati.nome) + "'? Verra spostato nel cestino, potrai recuperarlo dal NAS se serve.";
                  if (window.confirm(msg)) {
                    onElimina();
                  }
                }}
                style={{ ...btnSecondarioSt, color: theme.error, borderColor: theme.error + "55" }}
              >🗑 Elimina</button>
            )}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={onAnnulla} style={btnSecondarioSt}>Annulla</button>
            <button onClick={onSalva} style={btnPrimarioSt}>
              {isNew ? "+ Crea lavoratore" : "✓ Salva modifiche"}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}

// ============================================================================
// FORM SEZIONE 3: FORMAZIONE (tabella)
// ============================================================================
function FormFormazione({ dati, setDati }) {

  const toggle = (nome, colonna) => {
    const corrente = dati.formazione[nome] || {};
    setDati({
      ...dati,
      formazione: {
        ...dati.formazione,
        [nome]: { ...corrente, [colonna]: !corrente[colonna] },
      },
    });
  };

  return (
    <div style={{ padding: "14px 0", overflowX: "auto" }}>
      <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 10 }}>
        Metti la spunta sui corsi che ogni lavoratore ha seguito. Scorri orizzontalmente se non vedi tutte le colonne.
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 700 }}>
        <thead>
          <tr style={{ background: theme.bg, borderBottom: `1px solid ${theme.border}` }}>
            <th style={{ textAlign: "left", padding: "8px 10px", color: theme.textMuted, fontWeight: 600, position: "sticky", left: 0, background: theme.bg, minWidth: 150 }}>
              Lavoratore
            </th>
            {COLONNE_FORMAZIONE.map(c => (
              <th key={c.key} title={c.full} style={{ textAlign: "center", padding: "8px 4px", color: theme.textMuted, fontWeight: 600, fontSize: 11, minWidth: 50 }}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {OPERAI_POS.map(nome => {
            const riga = dati.formazione[nome] || {};
            return (
              <tr key={nome} style={{ borderBottom: `1px solid ${theme.border}44` }}>
                <td style={{ padding: "8px 10px", color: theme.text, fontWeight: 500, position: "sticky", left: 0, background: theme.surface }}>
                  {nome}
                </td>
                {COLONNE_FORMAZIONE.map(c => (
                  <td key={c.key} style={{ textAlign: "center", padding: "6px 4px" }}>
                    <input
                      type="checkbox"
                      checked={!!riga[c.key]}
                      onChange={() => toggle(nome, c.key)}
                      style={{ cursor: "pointer", width: 16, height: 16, accentColor: theme.accent }}
                    />
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================
// FORM SEZIONE 4: DPI (tabella)
// ============================================================================
function FormDpi({ dati, setDati }) {

  const toggle = (nome, colonna) => {
    const corrente = dati.dpi[nome] || {};
    setDati({
      ...dati,
      dpi: {
        ...dati.dpi,
        [nome]: { ...corrente, [colonna]: !corrente[colonna] },
      },
    });
  };

  return (
    <div style={{ padding: "14px 0", overflowX: "auto" }}>
      <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 10 }}>
        Indica quali DPI sono stati forniti a ogni lavoratore. Scorri orizzontalmente se non vedi tutte le colonne.
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 700 }}>
        <thead>
          <tr style={{ background: theme.bg, borderBottom: `1px solid ${theme.border}` }}>
            <th style={{ textAlign: "left", padding: "8px 10px", color: theme.textMuted, fontWeight: 600, position: "sticky", left: 0, background: theme.bg, minWidth: 150 }}>
              Lavoratore
            </th>
            {COLONNE_DPI.map(c => (
              <th key={c.key} title={c.full} style={{ textAlign: "center", padding: "8px 4px", color: theme.textMuted, fontWeight: 600, fontSize: 11, minWidth: 55 }}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {OPERAI_POS.map(nome => {
            const riga = dati.dpi[nome] || {};
            return (
              <tr key={nome} style={{ borderBottom: `1px solid ${theme.border}44` }}>
                <td style={{ padding: "8px 10px", color: theme.text, fontWeight: 500, position: "sticky", left: 0, background: theme.surface }}>
                  {nome}
                </td>
                {COLONNE_DPI.map(c => (
                  <td key={c.key} style={{ textAlign: "center", padding: "6px 4px" }}>
                    <input
                      type="checkbox"
                      checked={!!riga[c.key]}
                      onChange={() => toggle(nome, c.key)}
                      style={{ cursor: "pointer", width: 16, height: 16, accentColor: theme.accent }}
                    />
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================
// FORM SEZIONE 5: PONTEGGIO
// ============================================================================
function FormPonteggio({ dati, setDati }) {
  const p = dati.ponteggio;
  const aggiorna = (campo, val) => setDati({ ...dati, ponteggio: { ...p, [campo]: val } });

  return (
    <div style={{ padding: "14px 0" }}>
      <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 14 }}>
        Dati del ponteggio aziendale (se posseduto). Se non hai un ponteggio proprio, lascia vuoto.
      </div>
      <Griglia2col>
        <Campo label="Marca" value={p.marca} onChange={v => aggiorna("marca", v)} placeholder="es. Layher, Marcegaglia..." />
        <Campo label="Modello" value={p.modello} onChange={v => aggiorna("modello", v)} placeholder="es. Speedy Scaf..." />
      </Griglia2col>
      <Griglia2col>
        <Campo label="N° autorizzazione ministeriale" value={p.nAutorizzazione} onChange={v => aggiorna("nAutorizzazione", v)} />
        <div style={{ marginTop: 12 }}>
          <div style={labelSt}>Proprietà</div>
          <select style={inputSt} value={p.proprieta} onChange={e => aggiorna("proprieta", e.target.value)}>
            <option value="proprio">Proprio</option>
            <option value="nolo">A nolo</option>
            <option value="uso_comune">In uso comune</option>
          </select>
        </div>
      </Griglia2col>
    <MagazzinoPonteggio />
      </div>
  );
}

// ============================================================================
// FORM SEZIONE 6: ATTREZZATURE
// ============================================================================
function FormAttrezzature({ dati, setDati }) {

  const toggle = (key, campo) => {
    const corrente = dati.attrezzature[key] || { presente: false, libretto: false, collaudo: false, verifiche: false, usoComune: false };
    setDati({
      ...dati,
      attrezzature: {
        ...dati.attrezzature,
        [key]: { ...corrente, [campo]: !corrente[campo] },
      },
    });
  };

  return (
    <div style={{ padding: "14px 0" }}>
      <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 14 }}>
        Seleziona le attrezzature possedute dall'impresa. Per ciascuna, indica se hai libretto istruzioni, collaudo, verifiche periodiche.
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 600 }}>
          <thead>
            <tr style={{ background: theme.bg, borderBottom: `1px solid ${theme.border}` }}>
              <th style={{ textAlign: "left", padding: "8px 10px", color: theme.textMuted, fontWeight: 600, minWidth: 220 }}>Attrezzatura</th>
              <th style={{ textAlign: "center", padding: "8px 4px", color: theme.textMuted, fontWeight: 600, fontSize: 11 }}>Presente</th>
              <th style={{ textAlign: "center", padding: "8px 4px", color: theme.textMuted, fontWeight: 600, fontSize: 11 }}>Libretto</th>
              <th style={{ textAlign: "center", padding: "8px 4px", color: theme.textMuted, fontWeight: 600, fontSize: 11 }}>Collaudo</th>
              <th style={{ textAlign: "center", padding: "8px 4px", color: theme.textMuted, fontWeight: 600, fontSize: 11 }}>Verifiche</th>
              <th style={{ textAlign: "center", padding: "8px 4px", color: theme.textMuted, fontWeight: 600, fontSize: 11 }}>Uso com.</th>
            </tr>
          </thead>
          <tbody>
            {ATTREZZATURE_LISTA.map(a => {
              const riga = dati.attrezzature[a.key] || {};
              return (
                <tr key={a.key} style={{ borderBottom: `1px solid ${theme.border}44`, opacity: riga.presente ? 1 : 0.5 }}>
                  <td style={{ padding: "8px 10px", color: theme.text }}>{a.label}</td>
                  {["presente", "libretto", "collaudo", "verifiche", "usoComune"].map(campo => (
                    <td key={campo} style={{ textAlign: "center", padding: "6px 4px" }}>
                      <input
                        type="checkbox"
                        checked={!!riga[campo]}
                        onChange={() => toggle(a.key, campo)}
                        disabled={campo !== "presente" && !riga.presente}
                        style={{ cursor: "pointer", width: 16, height: 16, accentColor: theme.accent }}
                      />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================
// FORM SEZIONE 7: DOCUMENTI
// ============================================================================
function FormDocumenti({ dati, setDati }) {
  const d = dati.documenti;
  const aggiorna = (doc, campo, val) => setDati({
    ...dati,
    documenti: {
      ...d,
      [doc]: { ...(d[doc] || {}), [campo]: val },
    },
  });

  const documenti = [
    { key: "durc",       label: "DURC",                      descrizione: "Documento Unico di Regolarità Contributiva" },
    { key: "visura",     label: "Visura camerale",           descrizione: "Certificato Camera di Commercio" },
    { key: "inail",      label: "Iscrizione INAIL",          descrizione: "Attestato copertura infortuni" },
    { key: "inps",       label: "Iscrizione INPS",           descrizione: "Attestato iscrizione previdenza" },
    { key: "cassaEdile", label: "Iscrizione Cassa Edile",    descrizione: "Attestato Cassa Edile Pistoia" },
  ];

  return (
    <div style={{ padding: "14px 0" }}>
      <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 14 }}>
        Inserisci le date di scadenza dei documenti aziendali. L'upload dei PDF sarà aggiunto in futuro.
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {documenti.map(doc => {
          const scadenza = d[doc.key]?.scadenza || "";
          const scaduto = scadenza && new Date(scadenza) < new Date();
          return (
            <div key={doc.key} style={{ background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: 8, padding: 12 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 180px", gap: 14, alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: theme.text }}>{doc.label}</div>
                  <div style={{ fontSize: 11, color: theme.textDim, marginTop: 2 }}>{doc.descrizione}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: theme.textDim, marginBottom: 4, textTransform: "uppercase", letterSpacing: 1 }}>
                    Scadenza
                  </div>
                  <input
                    type="date"
                    value={scadenza}
                    onChange={e => aggiorna(doc.key, "scadenza", e.target.value)}
                    style={{ ...inputSt, padding: "8px 10px", borderColor: scaduto ? theme.error : theme.border }}
                  />
                  {scaduto && <div style={{ fontSize: 10, color: theme.error, marginTop: 4 }}>⚠️ Scaduto</div>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// HELPER COMPONENTS
// ============================================================================
function Campo({ label, value, onChange, type = "text", placeholder = "" }) {
  return (
    <div style={{ marginTop: 12 }}>
      <div style={labelSt}>{label}</div>
      <input
        type={type}
        value={value || ""}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={inputSt}
      />
    </div>
  );
}

function Griglia2col({ children }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, alignItems: "start" }}>
      {children}
    </div>
  );
}

function Griglia3col({ children }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, alignItems: "start", marginTop: 0 }}>
      {children}
    </div>
  );
}


// ============================================================================
// ScorciatoiaImpostazioni - mostra un riquadro che porta al tab Impostazioni
// ============================================================================
function ScorciatoiaImpostazioni({ icona, titolo, descrizione, tabImpostazioni, onNavigate }) {
  const handleClick = () => {
    if (typeof onNavigate === "function") {
      onNavigate("impostazioni", { tab: tabImpostazioni });
    } else {
      // Fallback: salva il tab desiderato in localStorage e naviga
      try {
        if (window.localStorage) {
          window.localStorage.setItem("imp_tab_iniziale", tabImpostazioni);
        }
      } catch(e){}
      window.location.hash = "#impostazioni";
      window.location.reload();
    }
  };
  return (
    <div style={{
      background:"#181b25", border:"1px solid #2a2e45", borderRadius:12, padding:24, textAlign:"center",
    }}>
      <div style={{ fontSize:42, marginBottom:12 }}>{icona}</div>
      <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:20, marginBottom:10 }}>{titolo}</div>
      <div style={{ fontSize:13, color:"#9aa0b3", marginBottom:24, maxWidth:560, margin:"0 auto 24px", lineHeight:1.5 }}>
        {descrizione}
      </div>
      <button
        onClick={handleClick}
        style={{
          background:"#f5a623", color:"#000", border:"none", padding:"10px 24px",
          borderRadius:10, fontWeight:700, cursor:"pointer", fontSize:14,
        }}
      >
        🔧 Configura in Impostazioni →
      </button>
      <div style={{ fontSize:11, color:"#6b6f80", marginTop:14, fontStyle:"italic" }}>
        Nelle prossime versioni la configurazione avverrà direttamente qui.
      </div>
    </div>
  );
}
